// frontend/src/pages/BacktestNovemberToAprilPage .js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import useDateRange from '../../hooks/useDateRange';
import useViewportCheck from '../../hooks/useViewportCheck'; 
import DateRangeControls from '../../components/DateRangeControls';
import Loading from '../../components/Loading';
import { createChartOptions } from '../../utils/chartOptions';
import { Typography, Card, Divider, Select, Space, Row, Col, Statistic, Tabs, Table, Collapse } from 'antd';
import { SettingOutlined, InfoCircleOutlined, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Title } = ResponsiveTypography;
const { Text, Paragraph } = Typography;

const { TabPane } = Tabs;
const { Panel } = Collapse;

const ASSET_NAME_MAP = {

    'KOSPI200': '코스피200 (KOSPI200)',
    'KOSDAQ': '코스닥 (KOSDAQ)',
    
    'S&P500': 'S&P500',
    "NASDAQ100": '나스닥100 (NASDAQ100)',
    "Russell2000": '미국 소형주 2000 지수 (Russell 2000)',
    "DowJones": '다우존스 산업평균지수 (Dow Jones)',

    "Nikkei225": '니케이225 (Nikkei225)',
    "HangSeng": '항셍지수 (HangSeng)',

    "Shanghai_Composite": '상해종합지수 (Shanghai_composite)',
    "TQQQ": '나스닥 3배 레버리지 ETF (TQQQ)',
    "UPRO": 'S&P500 3배 레버리지 ETF (UPRO)',
    "KODEX_Leveraged": '코스피 레버리지 ETF (KODEX 레버리지)',
    "KODEX_Semicon": '한국 반도체 ETF (KODEX 반도체)',

    "Gold" : '금 (Gold)',
    "Silver" : '은 (Silver)',
    "Copper" : '구리 (Copper)',

    "XLY": '경기소비재 섹터 ETF (XLY)',
    "XLU": '유틸리티 섹터 ETF (XLU)',
    "XLP": '필수소비재 섹터 ETF (XLP)',
    "XLF": '금융 섹터 ETF (XLF)',
    "XLE": '에너지 섹터 ETF (XLE)',
    "XLV": '헬스케어 섹터 ETF (XLV)',
    "XLI": '산업재 섹터 ETF (XLI)',
    "XLB": '소재 섹터 ETF (XLB)',
    "XLK": '기술 섹터 ETF (XLK)',
    "TLT": '미국 20년+ 장기국채 ETF (TLT)',

};

// --- 컴포넌트 섹션 ---

const StrategyDescription = () => (
    <div className="mb-8" style={{ padding: '0rem', marginBottom: '0.625rem' }}>
        <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>11월 매수 - 4월 매도 전략 </Title>
        <Paragraph  style={{ marginTop: '1.25rem', marginLeft: "0.4375rem", marginRight: "0.4375rem", textAlign: 'justify', wordBreak: 'break-all' }}>
            "5월에는 팔고 떠나라(Exit in May and go away)"는 월스트리트의 오랜 격언에 기반한 전략입니다. 
            이 전략은 주식 시장이 보통 11월부터 다음 해 4월까지 강세이고, 5월부터 10월까지는 약세 또는 횡보하는 경향이 있다는 계절적 패턴에 착안합니다.
            전략은 간단하게 <Text strong style={{ color: '#1890ff' }}>11월 초에 주식을 매수</Text>하고 <Text strong style={{ color: '#1890ff' }}>4월 말에 매도</Text>하여 이후 6개월간은 현금을 보유하는 방식입니다.
        </Paragraph>
        
        <Collapse ghost style={{ marginLeft: "-0.625rem", marginRight: "-0.625rem" }}>
            <Panel header="백테스팅 세팅 보기" key="1">
            <Paragraph  style={{ marginTop: '0rem',  marginBottom: '0rem', marginLeft: "0.625rem", marginRight: "1.25rem", textalign: 'left', wordBreak: 'keep-all', padding : '0rem 0rem' }}>

                <Text type="secondary">
                <ol style={{ paddingLeft: '0.625rem' }}>
                    <li>
                    백테스팅에 사용된 데이터는 yfinance에서 제공되는 주가정보를 기반으로 합니다.
                    </li>
                    <li>
                    매수는 매년 11월 첫 거래일에, 매도는 다음 해 4월 마지막 거래일에 진행하는 것으로 가정합니다.
                    </li>
                    <li>
                    거래비용은 0.25% 으로 설정했습니다.
                    </li>
                    <li>
                    '트레이딩 CAGR' 계산 시 전체 트레이딩의 시작일과 종료일 기준으로 계산하였기 때문에, 종목을 실제로 보유하는 기간만을 기준으로 한 '보유 기간 CAGR'은 '트레이딩 CAGR'보다 높을 수 있습니다. 이처럼 텍스트가 두 줄 이상으로 길어져도 시작점이 완벽하게 일치합니다.
                    </li>
                </ol>
                </Text>
            </Paragraph>
            </Panel>
        </Collapse>
    </div>
);


// Helper function to format numbers for table alignment
const formatNumber = (num) => {
    if (typeof num !== 'number' || !isFinite(num)) return '-';
    return num.toFixed(1);
};


const BacktestNovemberToAprilPage = () => {
    // Statistic 카드의 제목과 값의 글자 크기를 조절
    const isNarrow = useViewportCheck();
    const statisticTitleFontSize = isNarrow ? '0.8rem' : '0.9rem'; // 예: '12px', '1em' 등
    const statisticValueFontSize = isNarrow ? '1.2rem' : '1.4rem'; // 예: '20px', '1.5em' 등

    // State for selected assets, defaulting to KOSPI
    const [selectedStockMarket, setSelectedStockMarket] = useState('KOSPI200');

    // State for lists of options
    const [stockMarketList, setStockMarketList] = useState([]);

    // State for chart data and loading
    const [chartData, setChartData] = useState(null);
    const [tradingResults, setTradingResults] = useState([]);
    const [tradingStats, setTradingStats] = useState(null);
    const [annualReturns, setAnnualReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const chartRef = useRef(null);

    // Custom hook for date range management
    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(chartData?.stock_market_data, { dateColumn: 'Date', defaultPeriod: '10Y' });

    // Fetch lists for select boxes on component mount
    useEffect(() => {
        apiClient.get('/strategy-11to4/stock_list')
            .then(res => {
                setStockMarketList(res.data.stock_market_list.map(item => ({ 
                    value: item, 
                    label: ASSET_NAME_MAP[item] || item 
                })));
            })
            .catch(error => {
                console.error("Failed to fetch lists:", error);
            });
    }, []);

    // Fetch chart and trading data when selections change
    useEffect(() => {
        if (!stockMarketList.length ) return;

        setLoading(true);
        const fetchData = async () => {
            try {
                const params = {
                    stock_market_asset: selectedStockMarket,
                };

                const [chartRes, tradingRes, statsRes, annualRes] = await Promise.all([
                    apiClient.get('/strategy-11to4', { params }),
                    apiClient.get('/strategy-11to4/trading-results', { params }),
                    apiClient.get('/strategy-11to4/trading-statistics', { params }),
                    apiClient.get('/strategy-11to4/annual-returns', { params })
                ]);

                setChartData(chartRes.data);
                const sortedTradingResults = tradingRes.data.sort((a, b) => new Date(b['Exit Date']) - new Date(a['Exit Date']));
                setTradingResults(sortedTradingResults);
                setTradingStats(statsRes.data[0]);
                const sortedAnnualReturns = annualRes.data.sort((a, b) => b.Year - a.Year);
                setAnnualReturns(sortedAnnualReturns);

            } catch (error) {
                console.error("Failed to fetch data:", error);
                setChartData(null);
                setTradingResults([]);
                setTradingStats(null);
                setAnnualReturns([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedStockMarket, stockMarketList]);

    // Function to generate chart options
    const getChartOption = useCallback(() => {
        if (!isDateReady || !chartData) {
            return {};
        }
        
        const stockMarketLabel = ASSET_NAME_MAP[selectedStockMarket] || selectedStockMarket;

        const markPoints = [];
        const markLineData = [];
        if (tradingResults && tradingResults.length > 0) {
            tradingResults.forEach(trade => {
                const isProfit = trade['Return (%)'] > 0;
                const isHolding = trade['Holding'] === true;

                // 1. 모든 거래에 대해 '매수' 포인트를 추가합니다.
                markPoints.push({
                    name: '매수',
                    coord: [trade['Entry Date'], trade['Entry Price']],
                    symbol: 'triangle',
                    symbolRotate: 0,
                    symbolSize: 10,
                    itemStyle: { color: '#28a745' }
                });

                // 2. '보유 중' 상태와 '매도' 상태를 분기하여 처리합니다.
                if (isHolding) {
                    // '보유 중'일 때의 마커 설정
                    markPoints.push({
                        name: '보유 중',
                        coord: [trade['Exit Date'], trade['Exit Price']], // 'Exit Date'는 마지막 날짜를 의미
                        symbol: 'triangle',
                        symbolRotate: 180,
                        symbolSize: 10,
                        itemStyle: { color: '#505050' },
                        label: {
                            show: true,
                            formatter: `보유 중\n ${trade['Return (%)'].toFixed(1)}% `,
                            // position: isProfit ? 'top' : 'bottom',
                            position: 'top',
                            distance: 15,
                            fontSize: 12,
                            color: 'white',
                            backgroundColor: '#505050',
                            padding: [4, 2, 2, 2],
                            borderRadius: 4,
                        }
                    });
                } else {
                    // '매도' 완료일 때의 마커 설정 (기존과 동일)
                    markPoints.push({
                        name: '매도',
                        coord: [trade['Exit Date'], trade['Exit Price']],
                        symbol: 'triangle',
                        symbolRotate: 180,
                        symbolSize: 10,
                        itemStyle: { color: '#dc3545' },
                        label: {
                            show: true,
                            formatter: `${trade['Return (%)'].toFixed(1)}%`,
                            // position: isProfit ? 'top' : 'bottom',
                            position: 'top',
                            distance: 15,
                            fontSize: 12,
                            color: 'white',
                            backgroundColor: isProfit ? '#28a745' : '#dc3545',
                            padding: [4, 2, 2, 2],
                            borderRadius: 4,
                        }
                    });
                }
                markLineData.push([
                    {
                        coord: [trade['Entry Date'], trade['Entry Price']],
                        symbol: 'none'
                    },
                    {
                        coord: [trade['Exit Date'], trade['Exit Price']],
                        symbol: 'none',
                        lineStyle: {
                            color: '#505050',
                            width: 2,
                            type: 'dashed'
                        }
                    }
                ]);
            });
        }
        
        const finalOptions = {
            animation: true,
            legend: { data: [stockMarketLabel], top: '5%' },
            tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
            grid: { left: '5%', right: '10%', top: '15%', bottom: '12%', containLabel: true },
            xAxis: { type: 'time', boundaryGap: false },
            yAxis: { name: "종목 가격", type: 'value', scale: true, log:true, splitLine: { show: true }, axisLine: { show: true }},
            series: [
                {
                    name: stockMarketLabel,
                    type: 'line',
                    data: chartData.stock_market_data.map(d => [d.Date, d[selectedStockMarket]]),
                    showSymbol: false,
                    markPoint: { data: markPoints, silent: false },
                    markLine: {
                        silent: true, // 마크라인에 마우스 오버 이벤트를 비활성화
                        symbol: ['none', 'none'], // 라인의 시작과 끝에 심볼을 표시하지 않음
                        data: markLineData
                    }
                }
            ],
            dataZoom: [
                { type: 'slider', show: true, xAxisIndex: 0, bottom: '2%', startValue: viewStartDate?.toISOString(), endValue: viewEndDate?.toISOString() },
                { type: 'inside', xAxisIndex: 0 }
            ],
        };

        return createChartOptions(finalOptions);

    }, [isDateReady, chartData, viewStartDate, viewEndDate, selectedStockMarket, tradingResults]);
    
    const onDataZoom = useCallback(() => {
        if (chartRef.current) {
            const echartsInstance = chartRef.current.getEchartsInstance();
            const newOpt = echartsInstance.getOption();
            const startValue = newOpt.dataZoom[0].startValue;
            const endValue = newOpt.dataZoom[0].endValue;

            if (startValue && endValue) {
                const newStartDate = new Date(startValue);
                const newEndDate = new Date(endValue);
                
                if (newStartDate.getTime() !== viewStartDate?.getTime() || newEndDate.getTime() !== viewEndDate?.getTime()) {
                    handleDateChange([newStartDate, newEndDate]);
                }
            }
        }
    }, [viewStartDate, viewEndDate, handleDateChange]);

    const renderColoredCell = (text) => {
        if (typeof text !== 'number') return '-';
        const color = text > 0 ? 'green' : text < 0 ? 'red' : 'black';
        return (
            <span style={{ color: color }}>
                {formatNumber(text)}%
            </span>
        );
    };
    const renderCell = (text) => {
        if (typeof text !== 'number') return '-';
        return (
            <span style={{}}>
                {formatNumber(text)}%
            </span>
        );
    };
    const formatPrice = (text) => {
        const number = parseFloat(String(text).replace(/,/g, ''), 10);
        return isNaN(number) ? text : number.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });
    };


    const tradingHistoryColumns = [
        { title: '수익률', dataIndex: 'Return (%)', key: 'return', align: 'right',render: renderColoredCell},
        { title: '진입일', dataIndex: 'Entry Date', key: 'EntryDate', render: (text) => dayjs(text).format('YYYY-MM-DD'), align: 'right'},
        { title: '진입가격', dataIndex: 'Entry Price', key: 'EntryPrice', render: formatPrice, align: 'right'},
        { title: '청산일', dataIndex: 'Exit Date', key: 'ExitDate', render: (text) => dayjs(text).format('YYYY-MM-DD'), align: 'right' },
        { title: '청산가격', dataIndex: 'Exit Price', key: 'ExitPrice',render: formatPrice, align: 'right' },
        { title: 'Drawdown 발생일', dataIndex: 'Trough Date', key: 'troughDate', render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-', align: 'right'},
        { title: 'Drawdown', dataIndex: 'Drawdown (%)', key: 'drawdown', render: (text) => `${formatNumber(text)}%`, align: 'right' },
    ];

    const annualPerformanceColumns = [
        { title: '연도', dataIndex: 'Year', key: 'year', align: 'center' },
        { 
            title: '전략 수익률', 
            dataIndex: 'Annual Return (%)', 
            key: 'annualReturn', 
            align: 'right',
            render: renderColoredCell
        },
        { 
            title: '단순보유 수익률', 
            dataIndex: 'Benchmark Return (%)', 
            key: 'benchmarkReturn', 
            align: 'right',
            render: renderColoredCell
        },
        {
            title: '초과 수익률',
            key: 'excessReturn',
            align: 'right',
            render: (text, record) => {
                const annualReturn = record['Annual Return (%)'];
                const benchmarkReturn = record['Benchmark Return (%)'];
                if (typeof annualReturn !== 'number' || typeof benchmarkReturn !== 'number') {
                    return '-';
                }
                const excessReturn = annualReturn - benchmarkReturn;
                return renderColoredCell(excessReturn);
            }
        },
        { 
            title: '전략 Drawdown', 
            dataIndex: 'Annual Drawdown (%)', 
            key: 'annualDrawdown', 
            align: 'right',
            render: renderCell
        },
        { 
            title: '밴치마크 Drawdown', 
            dataIndex: 'Benchmark Drawdown (%)', 
            key: 'benchmarkDrawdown', 
            align: 'right',
            render: renderCell
        },
    ];

    const paginationConfig = {
        defaultPageSize: 10, // 기본적으로 보여줄 줄 수
        showSizeChanger: true, // 줄 수 변경 옵션 표시 여부 (이게 핵심입니다!)
        pageSizeOptions: ['10', '20', '50', '100'], // 선택 가능한 줄 수 옵션
        position: ['bottomCenter'], // 페이지네이션 위치 (선택사항)
    };


    if (loading || !chartData) {
        return (
            <Loading />
        );
    }

    return (
        <div className="content-box">
            <StrategyDescription />

            {tradingStats && (
                <Card style={{ marginBottom: '1rem' }} bodyStyle={{ padding: '0.3125rem 0.3125rem' }}>
                    <Row gutter={[5, 10]}>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic
                                title={<span style={{ fontSize: statisticTitleFontSize }}>트레이딩 누적 수익률</span>}
                                value={tradingStats['Cumulative Return (%)']} 
                                precision={0} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>트레이딩 CAGR</span>}
                                value={tradingStats['CAGR (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>보유 기간 CAGR</span>}
                                value={tradingStats['Holding CAGR (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>단순 장기투자 CAGR</span>}
                                value={tradingStats['BNP CAGR (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>트레이딩 수익 확률</span>}
                                value={tradingStats['Win Rate (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>전략 MDD</span>}
                                value={tradingStats['Strategy MDD (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>T-통계량</span>}
                                value={tradingStats['T-statistic']} 
                                precision={1} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>P-값(%)</span>}
                                value={tradingStats['P-value (%)']} 
                                precision={2} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>샤프 지수</span>}
                                value={tradingStats['Sharpe Ratio']} 
                                precision={2} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>총 거래 횟수</span>}
                                value={tradingStats['Total Trades']} 
                                suffix="회" 
                                precision={0} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>총 투자 기간</span>}
                                value={tradingStats['Total Period (Years)']} 
                                suffix="년" 
                                precision={1} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>총 보유 기간</span>}
                                value={tradingStats['Total Holding Period (Years)']} 
                                suffix="년" 
                                precision={1} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            <Card style={{ marginTop: '1rem' }} bodyStyle={{ padding: '0.625rem' }}>
                <Row gutter={[10, 10]} align="middle">
                    <Col xs={24} sm={24}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong><SettingOutlined /> 트레이딩 종목 </Text>
                            <Select showSearch value={selectedStockMarket} onChange={setSelectedStockMarket} style={{ width: '100%' }} options={stockMarketList}
                                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card style={{ marginTop: '0.625rem', padding: '0rem', marginLeft: '0rem', marginRight: '0rem' }} bodyStyle={{ padding: '0.625rem' }}>
                <Title level={4} style={{ padding: '0.625rem 0' }}>매수 / 매도 기록 차트 </Title>
                <Divider style={{ margin: 0 }}/>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.625rem', marginBottom : '0.625rem', marginLeft: '0.625rem', gap: '0.625rem' }}>
                    <Text strong style={{ fontSize: '1.1em' }}><CaretUpOutlined style={{ color: '#28a745', fontSize: '1.2em', verticalAlign: 'middle', marginRight: '0.25rem' }} />매수 </Text>
                    <Text strong style={{ fontSize: '1.1em' }}><CaretDownOutlined style={{ color: '#dc3545', fontSize: '1.2em', verticalAlign: 'middle', marginRight: '0.25rem' }} />매도 </Text>
                    <Text strong style={{ fontSize: '1.1em' }}><CaretDownOutlined  style={{ color: '#505050', fontSize: '1.2em', verticalAlign: 'middle', marginRight: '0.25rem' }} /> 보유 중 </Text>
                </div>

                <ReactECharts ref={chartRef} option={getChartOption()} style={{ height: '37.5rem' }} showLoading={loading} notMerge={true} onEvents={{ 'datazoom': onDataZoom }} />
                <Divider />
                <DateRangeControls minDate={minDate} maxDate={maxDate} viewStartDate={viewStartDate} viewEndDate={viewEndDate} onPeriodChange={handlePeriodChange} onDateChange={handleDateChange} />
            </Card>

            <Card style={{ marginTop: '0.75rem' }}>
                <Tabs defaultActiveKey="1">
                    <TabPane tab="거래 기록" key="1">
                        <Table dataSource={tradingResults} columns={tradingHistoryColumns} rowKey={(record, index) => index} size="small" scroll={{ x: 'max-content' }} pagination={paginationConfig} />
                    </TabPane>
                    <TabPane tab="연간 성과 비교" key="2">
                        <Table dataSource={annualReturns} columns={annualPerformanceColumns} rowKey="Year" size="small" scroll={{ x: 'max-content' }} pagination={paginationConfig} />
                    </TabPane>
                </Tabs>
            </Card>

            <Card size="small"  style={{ background: '#f8f9fa', marginTop: '1rem', marginBottom: '1.25rem' }}>
                <Title level={5}><InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }}/> 주의사항</Title>
                <Paragraph  style={{ marginTop: '1.25rem', marginLeft: "0.4375rem", marginRight: "0.4375rem", textalign: 'left', wordBreak: 'keep-all' }}>
                    본 백테스트 결과는 과거의 데이터를 기반으로 한 것으로, 미래의 수익을 보장하지 않습니다. 
                    계절성 전략은 시장의 구조적 변화나 예기치 않은 경제 이벤트에 의해 그 효과가 감소하거나 사라질 수 있습니다.
                    <Text strong style={{ color: '#1890ff' }}> 충분한 검토 후</Text> 투자 결정을 내리시기 바랍니다.
                </Paragraph>
            </Card>
        </div>
    );
};

export default BacktestNovemberToAprilPage;